import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

function highlight(code) {
  const kws = ["oc","kubectl","apply","get","describe","create","delete","patch","edit","adm",
    "import","from","as","def","class","return","for","in","if","else","True","False","None",
    "watch","export","echo","cat","chroot","systemctl","journalctl","rpm-ostree","etcdctl","crictl"];
  return code.split("\n").map((line, li) => {
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i] === "#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i = line.length; continue;
      }
      if (line[i] === '"' || line[i] === "'") {
        const q = line[i]; let j = i+1;
        while (j < line.length && !(line[j]===q && line[j-1]!=="\\")) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i = j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j = i;
        while (j<line.length && /[\d._]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i = j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j = i;
        while (j<line.length && /[\w-]/.test(line[j])) j++;
        const word = line.slice(i,j);
        let color = T.textPri;
        if (kws.includes(word)) color = T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i = j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textPri}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="bash" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"6px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"14px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Concept({ children }) {
  return (
    <div style={{background:T.card,borderLeft:`3px solid ${T.accent}`,borderRadius:"0 8px 8px 0",
      padding:"12px 16px",marginBottom:14,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}
function Analogy({ icon="💡", title, children }) {
  return (
    <div style={{background:"#0F1E2E",border:"1px solid #1E3A52",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.accent,letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>
        {icon} {title || "Analogy"}
      </div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}
function ASCII({ children }) {
  return (
    <pre style={{background:"#07111E",border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:14,fontSize:11.5,color:"#7DD3FC",
      lineHeight:1.6,overflowX:"auto",fontFamily:"'JetBrains Mono','Fira Mono',monospace",whiteSpace:"pre"}}>
      {children}
    </pre>
  );
}
function WarnBox({ items }) {
  return (
    <div style={{background:"#1C1506",border:"1px solid #78350F",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.yellow,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>⚠ Watch Out</div>
      {items.map((it,i)=><div key={i} style={{fontSize:13,color:"#D4B483",marginBottom:5,lineHeight:1.5}}>{it}</div>)}
    </div>
  );
}
function InfoBox({ title, color=T.accent, bgColor="#071929", borderColor="#1E3A52", children }) {
  return (
    <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>{title}</div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}
function InterviewQ({ items }) {
  const [open, setOpen] = useState({});
  return (
    <div style={{background:"#071929",border:"1px solid #1E3A52",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:"#60A5FA",letterSpacing:1,marginBottom:10,textTransform:"uppercase"}}>Interview Questions</div>
      {items.map((item,i)=>(
        <div key={i} style={{marginBottom:8}}>
          <div onClick={()=>setOpen(o=>({...o,[i]:!o[i]}))}
            style={{cursor:"pointer",display:"flex",gap:8,alignItems:"flex-start",fontSize:13,color:T.textPri,fontWeight:500,lineHeight:1.5}}>
            <span style={{color:"#60A5FA",minWidth:16,marginTop:1}}>{open[i]?"▾":"▸"}</span>
            <span>{item.q}</span>
          </div>
          {open[i]&&<div style={{fontSize:12.5,color:T.textSec,marginTop:6,paddingLeft:24,lineHeight:1.65}}>{item.a}</div>}
        </div>
      ))}
    </div>
  );
}
function TTable({ headers, rows }) {
  return (
    <div style={{overflowX:"auto",marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5,fontFamily:"inherit"}}>
        <thead>
          <tr>{headers.map((h,i)=>(
            <th key={i} style={{background:"#0D1929",color:T.accent,padding:"8px 12px",
              borderBottom:`1px solid ${T.border}`,textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>
              {h}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri} style={{background:ri%2===0?T.card:T.surface}}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{padding:"7px 12px",color:T.textSec,
                  borderBottom:`1px solid ${T.border}33`,lineHeight:1.5,verticalAlign:"top"}}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── SECTION COMPONENTS ───────────────────────────────────────────────────────

function S1() { return (
  <div>
    <Concept>Kubernetes ek <strong style={{color:T.accent}}>engine</strong> hai — bahut powerful, lekin <strong style={{color:T.red}}>car nahi</strong> hai. Plain upstream Kubernetes se production chalana ho toh aapko yeh sab khud jodna padega:</Concept>
    <TTable headers={["Cheez","Plain K8s mein kya hota hai"]} rows={[
      ["OS on nodes","Aap khud RHEL/Ubuntu install karo, patch karo, drift handle karo"],
      ["Ingress","Khud nginx/HAProxy ingress controller deploy karo"],
      ["Monitoring","Khud Prometheus + Grafana + Alertmanager stack banao"],
      ["Logging","Khud EFK/Loki lagao"],
      ["Registry","Khud Harbor/registry chalao"],
      ["Auth","Khud OIDC/Dex/Keycloak wire karo"],
      ["Upgrade","Har component alag-alag, compatibility matrix aap sambhalo"],
      ["Security default","Default mein root container chal jaata hai 😬"],
      ["Certificates","Khud rotate karo"],
      ["Support","Community. Prod down? Google karo."],
    ]} />
    <WarnBox items={[
      'Ye "DIY Kubernetes" Day-1 pe kaam karta hai, Day-500 pe rota hai. Har cluster alag ho jaata hai — configuration drift. 200 clusters ho gaye? Ab 200 snowflakes hain.',
    ]} />
    <InfoBox title="Business Problem" color={T.green} bgColor="#0a1a10" borderColor="#1E3A52">
      Enterprise ko chahiye ek <strong style={{color:T.textPri}}>opinionated, secure-by-default, self-healing, single-vendor-supported, upgradable-as-one-unit</strong> Kubernetes platform — jise regulated industries (bank, telecom, healthcare, government) audit kar sakein.
      <br/><br/>
      <strong style={{color:T.accent}}>OpenShift Container Platform (OCP)</strong> = Kubernetes + battery-included platform + Red Hat support + lifecycle guarantee.
    </InfoBox>
    <InfoBox title="Ek Line Mein" color={T.purple} bgColor="#100a1a" borderColor="#2A1A45">
      Kubernetes aapko <em>primitives</em> deta hai. OpenShift aapko <em>product</em> deta hai.
    </InfoBox>
    <InterviewQ items={[
      {q:"OCP vs vanilla Kubernetes — kya farak hai?", a:"OCP = Kubernetes + integrated platform (registry, monitoring, ingress, auth, console, CI/CD) + secure-by-default (SCC, SELinux) + self-managing lifecycle (CVO, Operators) + Red Hat support. Vanilla K8s = engine only."},
      {q:"OCP kab choose karein, EKS/AKS kab?", a:"OCP: on-prem/hybrid, regulated industries, need full control + single upgrade unit + bare-metal. EKS/AKS/GKE: pure cloud, less ops burden, okay with shared responsibility model."},
    ]} />
  </div>
);}

function S2() { return (
  <div>
    <Concept>OpenShift 5 badi problems solve karta hai — ye tab yaad aayega jab koi pooche "OpenShift kyon?"</Concept>
    {[
      {n:1,t:"Assembly Problem",c:T.accent,d:"Sab kuch pehle se integrated — registry, monitoring, logging, ingress, auth, console, CI/CD. Zero DIY."},
      {n:2,t:"Security-by-Default Problem",c:T.green,d:"SCC (SecurityContextConstraints) ki wajah se by default container root nahi chal sakta. Random UID milta hai. SELinux enforced."},
      {n:3,t:"Drift Problem",c:T.yellow,d:'Node ka OS (RHCOS) immutable hai. Config machine-level pe declaratively push hoti hai (MachineConfig). Koi engineer SSH karke "yum install" nahi kar sakta — platform usko revert/degrade dikhayega.'},
      {n:4,t:"Upgrade Problem",c:T.orange,d:"Poora cluster ek unit ki tarah upgrade hota hai — ek button, ek version number. Control plane + OS + operators + networking, sab. Yahi OCP4 ka #1 differentiator hai."},
      {n:5,t:"Day-2 Problem",c:T.purple,d:"Sab kuch Operator se manage hota hai. Operator = automated SRE encoded as software."},
    ].map(p=>(
      <div key={p.n} style={{background:T.card,border:`1px solid ${p.c}44`,borderLeft:`3px solid ${p.c}`,
        borderRadius:"0 8px 8px 0",padding:"12px 16px",marginBottom:12}}>
        <div style={{fontSize:10,fontWeight:700,color:p.c,letterSpacing:1,marginBottom:4,textTransform:"uppercase"}}>Problem {p.n} — {p.t}</div>
        <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{p.d}</div>
      </div>
    ))}
  </div>
);}

function S3() { return (
  <div>
    <Analogy icon="🚗" title="Engine vs Car">
      <strong style={{color:T.textPri}}>Kubernetes</strong> = Engine. Bohot strong, lekin usme steering, brakes, airbags, dashboard nahi.<br/><br/>
      <strong style={{color:T.textPri}}>OpenShift</strong> = Poori car (Mercedes). Engine wahi Kubernetes ka hai, lekin safety, dashboard (Console), airbags (SCC), service warranty (Red Hat support) sab included.<br/><br/>
      <strong style={{color:T.textPri}}>EKS/AKS/GKE</strong> = Ola/Uber. Car aapki nahi, aap sirf baithte ho. Control kam, effort kam.
    </Analogy>
    <Analogy icon="🏛" title="OpenShift ka 'Sarkar' Model — Operators samajhne ke liye">
      <div style={{marginBottom:8}}>Socho cluster ek <strong style={{color:T.accent}}>desh</strong> hai:</div>
      <TTable headers={["Cluster cheez","Desh mein kya"]} rows={[
        [<strong style={{color:T.accent}}>etcd</strong>,"Sarkar ka record room / land registry. Sach ka ek hi source."],
        [<strong style={{color:T.accent}}>API Server</strong>,"Parliament ka main gate + reception. Har request yahin se aati hai. ID check (authn), permission check (authz), rule check (admission)."],
        [<strong style={{color:T.accent}}>Scheduler</strong>,"Housing allotment officer. 'Naya bandaa aaya, kis flat (node) mein daalein?'"],
        [<strong style={{color:T.accent}}>Controller Manager</strong>,"Sarkari babus. Har babu ek kaam dekhta hai: 'Ye register kehta hai 3 hone chahiye, ground pe 2 hain → 1 aur bulao.'"],
        [<strong style={{color:T.accent}}>Kubelet</strong>,"Har building ka watchman. Apni building (node) ka charge."],
        [<strong style={{color:T.accent}}>CRI-O</strong>,"Watchman ka staff jo actually kamre khol-band karta hai (container start/stop)."],
        [<strong style={{color:T.accent}}>CVO-(Cluster Version Operator)</strong>,"Prime Minister. Poore desh ki policy version decide karta hai.Its job is to keep the entire OpenShift cluster at the desired version."],
        [<strong style={{color:T.accent}}>Cluster Operators (~35)</strong>,"Ministries. Har ministry apna department chalati hai (networking, auth, monitoring)."],
        [<strong style={{color:T.accent}}>MCO</strong>,"PWD department. Sadak (node OS) ka nirmaan aur maintenance."],
        [<strong style={{color:T.accent}}>OLM - (Operator Lifecycle management)</strong>,"Play Store. Third-party apps (operators) install/upgrade/uninstall."],
        [<strong style={{color:T.accent}}>RHCOS</strong>,"Har building ki fixed, sealed structure. Renovation allowed nahi — sirf poora rebuild."],
      ]} />
      <div style={{fontSize:12.5,color:T.textMute,marginTop:8,fontStyle:"italic"}}>Is analogy ko pakad ke rakho — poore module mein kaam aayegi.</div>
    </Analogy>
  </div>
);}

function S4() { return (
  <div>
    <Concept>5 minutes mein OCP ka poora evolution — interview mein poocha jaata hai.</Concept>
    <TTable headers={["Version","Saal","Kya tha"]} rows={[
      ["OpenShift v1/v2","2011–2013","Kubernetes se pehle! 'Gears' & 'Cartridges' — custom PaaS, SELinux-based isolation"],
      ["OpenShift v3","2015","Kubernetes + Docker adopt kiya. Ansible se install hota tha. RHEL nodes. Upgrade = Ansible playbook = dard 😅"],
      [<strong style={{color:T.accent}}>OpenShift v4</strong>,"2019","Bilkul re-architect. CoreOS acquisition (2018). Sab kuch Operator-driven. RHCOS immutable OS. Ignition. CVO. Self-managing cluster."],
      ["OCP 4.20","2025","4.x line continue. OVN-Kubernetes default, cgroup v2, CRI-O runtime, Operators everywhere, hosted control planes mature."],
    ]} />
    <InfoBox title="Sabse Bada Takeaway" color={T.orange} bgColor="#1a0f06" borderColor="#78350F">
      v3 → v4 mein <strong style={{color:T.textPri}}>install aur upgrade ka philosophy hi badal gaya.</strong><br/>
      v3: "Main (Ansible) cluster ko <em>banata</em> hoon."<br/>
      v4: "Cluster khud ko <em>banaye rakhta hai</em> (self-managing)." Aap sirf desired version bataate ho, CVO baaki sambhalta hai.
    </InfoBox>
    <InfoBox title="Architect Interview Line" color={T.purple} bgColor="#100a1a" borderColor="#2A1A45">
      "OCP4's core idea is that the cluster is a Kubernetes application that manages itself using Operators, with the CVO as the root controller."
    </InfoBox>
    <InterviewQ items={[
      {q:"OCP3 aur OCP4 ka sabse bada farak kya hai?",a:"OCP3: Ansible-based install + mutable RHEL nodes + Docker runtime + manual upgrades. OCP4: openshift-install binary + immutable RHCOS + CRI-O + Operator-driven self-managing lifecycle. Philosophy shift: cluster ab khud ko manage karta hai."},
    ]} />
  </div>
);}

function S5() { return (
  <div>
    <Concept>OCP cluster do planes mein divided hai — Control Plane (brain) aur Worker Nodes (muscles). Har component ka role clear aur separated hai.</Concept>
    <ASCII>{`┌──────────────── CONTROL PLANE (3 nodes, always odd) ────────────────┐
│                                                                      │
│  kube-apiserver   etcd   kube-scheduler   kube-controller-manager   │
│  openshift-apiserver   oauth-apiserver   openshift-controller-mgr   │
│  (+ kubelet + CRI-O bhi chalte hain — control plane bhi node hi hai)│
│                                                                      │
└──────────────────────────────────────────────────────────────────────┘
                              ▲
                              │ kubelet watch/report every 10s
                              ▼
┌──────────────────── WORKER / COMPUTE NODES (n) ──────────────────────┐
│  kubelet → CRI-O → containers                                        │
│  OVN-Kubernetes (ovnkube-node) | node-exporter | DNS | MCD           │
└──────────────────────────────────────────────────────────────────────┘`}</ASCII>
    <InfoBox title="OCP-Specific: Control Plane Schedulability">
      OCP mein control plane nodes <strong style={{color:T.red}}>by default schedulable nahi</strong> hote (workloads nahi chalte).
      Sirf 3-node compact cluster (edge/telco use case) mein masters worker bhi bante hain — lekin tab
      strict resource reservation (<code style={{color:T.accent}}>systemReserved</code>, <code style={{color:T.accent}}>kubeReserved</code>) mandatory hai,
      warna etcd starve ho jaayega.
    </InfoBox>
    <ASCII>{`COMPONENT OWNERSHIP:
                    ┌─────────────────────────────────────────┐
                    │  CVO (Cluster Version Operator)          │
                    │  "PM — declares desired state for all"   │
                    └───────────────┬─────────────────────────┘
                                    │ manages ~35 Cluster Operators
          ┌─────────────────────────┼──────────────────────────┐
          ▼                         ▼                          ▼
   authentication              monitoring                 machine-config
   network                     ingress                    etcd
   image-registry              kube-apiserver             dns  ...`}</ASCII>
    <InterviewQ items={[
      {q:"Control plane ke kitne nodes hone chahiye aur kyun?",a:"3 ya 5 (odd). Reason: etcd Raft quorum = N/2+1. 3 nodes mein 1 fail ho toh cluster chal jaata hai. 2 fail = dead. Even number kabhi nahi — split-brain ho sakta hai."},
      {q:"Control plane nodes pe application pods kyon nahi chalte by default?",a:"Noisy neighbor isolation — workload pressure se etcd/apiserver ko protect karna. Compact clusters (3-node edge) mein taint + toleration se allowed karte hain, lekin tightly resource-managed."},
    ]} />
  </div>
);}

function S6() { return (
  <div>
    <Concept>API Server OCP ka ekmatra darwaza hai — sirf yahi etcd se baat karta hai. Har request is pipeline se guzarti hai.</Concept>
    <InfoBox title="Request Pipeline — A-A-M-V-P mnemonic">
      <strong style={{color:T.accent}}>A</strong>uthentication → <strong style={{color:T.accent}}>A</strong>uthorization → <strong style={{color:T.accent}}>M</strong>utating Admission → <strong style={{color:T.accent}}>V</strong>alidation → <strong style={{color:T.accent}}>V</strong>alidating Admission → <strong style={{color:T.accent}}>P</strong>ersist
      <br/><br/><em style={{color:T.textMute}}>"Aam khaya, phir VP ban gaya" 🥭</em>
    </InfoBox>
    <TTable headers={["Step","Kya hota hai","Example"]} rows={[
      ["Authentication","Tu kaun hai?","cert / bearer token / OAuth token"],
      ["Authorization","Tu ye kar sakta hai?","RBAC — can user create pods in namespace?"],
      ["Mutating Admission","Object ko badla jaaye?","SCC UID inject, sidecar inject, default limits"],
      ["Validation","Schema sahi hai?","Required fields present? Types correct?"],
      ["Validating Admission","Policy allow karti hai?","ResourceQuota, PodSecurity, OPA/Gatekeeper"],
      ["Persist","etcd mein likh do.","Final write — ab ye 'sach' hai"],
    ]} />
    <InfoBox title="OCP mein 3 API Servers hote hain — Interview Gold" color={T.yellow} bgColor="#1a1506" borderColor="#78500F">
      <strong style={{color:T.accent}}>kube-apiserver</strong> → standard K8s objects (Pod, Deployment, Service)<br/>
      <strong style={{color:T.accent}}>openshift-apiserver</strong> → OpenShift objects (Route, Project, BuildConfig, ImageStream)<br/>
      <strong style={{color:T.accent}}>oauth-apiserver</strong> → OAuth objects (User, Identity, OAuthAccessToken)<br/><br/>
      Ye teeno <strong style={{color:T.textPri}}>aggregated API</strong> ke through ek hi endpoint jaise dikhte hain (<code style={{color:T.accent}}>:6443</code>).
    </InfoBox>
    <InterviewQ items={[
      {q:"kube-apiserver stateless kyun hai?",a:"Apna koi state nahi rakhta — sab kuch etcd mein hai. Isliye horizontally scale ho sakta hai (3 replicas, ek per master). Koi bhi replica koi bhi request handle kar sakta hai."},
      {q:"Mutating vs Validating admission webhook mein kya farak hai?",a:"Mutating: object ko modify kar sakta hai (e.g. default inject karna, UID assign karna). Validating: sirf allow/deny — modify nahi kar sakta. Mutating pehle chalta hai, phir validating."},
    ]} />
  </div>
);}

function S7() { return (
  <div>
    <Concept>etcd cluster ka <strong style={{color:T.accent}}>single source of truth</strong> hai — distributed key-value store using Raft consensus. Disk latency isko directly impact karti hai.</Concept>
    <TTable headers={["Topic","Detail"]} rows={[
      ["Protocol","Raft consensus — leader election, log replication"],
      ["Quorum formula","(N/2) + 1. 3 nodes → 1 fail OK. 5 nodes → 2 fail OK."],
      ["Disk requirement","NVMe/SSD mandatory. fsync p99 < 10ms. Spinning disk = cluster instability."],
      ["Data encryption","APIServer CR → encryption: type: aescbc/aesgcm"],
      ["Backup","cluster-backup.sh → snapshot + static pod manifests"],
      ["Size limit","Default quota: 2GB (8GB max). Tune-able. Defrag periodically."],
    ]} />
    <CodeBlock code={`# etcd health check (rsh mein jaake chalao)
oc get pods -n openshift-etcd
oc rsh -n openshift-etcd etcd-<master-0>
etcdctl endpoint status --cluster -w table
etcdctl endpoint health --cluster

# disk latency check (Prometheus)
# etcd_disk_wal_fsync_duration_seconds bucket{quantile="0.99"} < 0.01`} label="bash — etcd ops" />
    <WarnBox items={[
      "etcd disk latency is the #1 cause of mysterious OCP instability. 'Cluster slow hai, pods evict ho rahe, API server timeout' — 90% chance etcd ki disk slow hai.",
      "Spinning disk pe etcd kabhi nahi. SATA SSD bhi edge case mein fail hoti hai high I/O workload ke saath. NVMe non-negotiable.",
    ]} />
    <InterviewQ items={[
      {q:"etcd even number kyun nahi?",a:"Even number mein split-brain possible hai. 4 nodes: do group of 2 — dono sochte hain woh majority hai. Raft quorum odd mein guaranteed majority hota hai."},
      {q:"etcd ka backup kab aur kaise?",a:"Daily automated backup via cluster-backup.sh. Snapshot + static pod manifests + kubeconfig. S3/NFS pe store karo. Restore ke liye separate procedure hai (Module 13 mein deep jaayenge)."},
    ]} />
  </div>
);}

function S8() { return (
  <div>
    <Concept>Scheduler pod ko node se match karta hai (start nahi karta). Controller Manager reconciliation loops chalata hai — desired vs actual state ko converge karta hai.</Concept>
    <InfoBox title="Scheduler — Do Phase">
      <strong style={{color:T.accent}}>Phase 1: Filtering (Predicates)</strong> — kaunse nodes possible hain?
      (resources hain? taint tolerate hoti hai? nodeSelector match? volume attach ho sakta hai?)<br/><br/>
      <strong style={{color:T.accent}}>Phase 2: Scoring (Priorities)</strong> — best node kaunsa?
      (least loaded, image already present, affinity, topology spread)<br/><br/>
      Highest score wale node pe <strong style={{color:T.textPri}}>binding</strong> likh deta hai etcd mein.
      Scheduler khud pod <strong style={{color:T.red}}>start nahi karta</strong> — sirf pod.spec.nodeName set karta hai. Actual start kubelet karta hai.
    </InfoBox>
    <InfoBox title="Controller Manager — Reconciliation Loop">
      <div style={{fontFamily:"monospace",fontSize:12,color:T.codeKw,background:T.codeBg,padding:"10px 12px",borderRadius:6}}>
        {`loop forever:
    desired = read from API server (spec)
    actual  = observe world (status)
    if desired != actual:
        take action to converge`}
      </div>
      <div style={{marginTop:10}}>Ye <strong style={{color:T.textPri}}>reconciliation loop</strong> Kubernetes ka dil hai. <strong style={{color:T.accent}}>Operator bhi yahi cheez hai</strong> — bas custom resource ke liye.</div>
    </InfoBox>
    <TTable headers={["Controller","Kaam"]} rows={[
      ["deployment-controller","Deployment → ReplicaSet banata hai"],
      ["replicaset-controller","RS → Pods banata hai, desired count maintain karta hai"],
      ["node-controller","Node NotReady? Pods evict karo (taints + eviction)"],
      ["job-controller","Job completion track karta hai"],
      ["endpoint-controller","Service → Pod IP mapping update karta hai"],
      ["openshift-controller-manager","Build, ImageStream, DeploymentConfig controllers (OCP-specific)"],
    ]} />
    <InterviewQ items={[
      {q:"Ek pod create kiya but Pending hai. Scheduler ne kya kiya?",a:"Scheduler filtering mein sab nodes fail ho gayi. oc describe pod → Events section mein scheduler ka reason milega: '0/5 nodes available: 3 Insufficient cpu, 2 node(s) had taint...'"},
      {q:"Deployment controller aur ReplicaSet controller alag kyun hain?",a:"Separation of concerns. Deployment controller rolling update, rollback logic handle karta hai. ReplicaSet controller sirf pod count maintain karta hai. Compose karke zyada powerful, test karne mein alag-alag easier."},
    ]} />
  </div>
);}

function S9() { return (
  <div>
    <Concept>Worker node pe 5 key components hain. Inka role aur interaction samajhna debugging ke liye critical hai.</Concept>
    <TTable headers={["Component","Kaam","Debug command"]} rows={[
      ["kubelet","Node ka agent. API server se apne node ke pods watch karta hai, CRI-O ko chalwata hai. Health probes. Node status report (har 10s).","oc adm node-logs <node> -u kubelet"],
      ["CRI-O","Container runtime. Docker NAHI. Lightweight, sirf K8s ke liye. CRI spec implement karta hai. runc/crun use karta hai.","crictl ps, crictl logs, crictl inspect"],
      ["OVN-Kubernetes (ovnkube-node)","Default CNI. Service/network implement karta hai via OpenFlow rules. Traditional iptables kube-proxy ki jagah.","oc get pods -n openshift-ovn-kubernetes"],
      ["MCD (machine-config-daemon)","Har node pe DaemonSet. MCO se config leta hai, apply karta hai, reboot karta hai.","oc logs -n openshift-machine-config-operator ds/machine-config-daemon"],
      ["CSI drivers / node-exporter / DNS","Storage, metrics, DNS resolution","varies by component"],
    ]} />
    <WarnBox items={[
      "Docker OCP 4 mein hai hi NAHI. Debug ke liye crictl use karo. Interview mein bahut pucha jaata hai.",
      "crictl ps (running containers), crictl ps -a (all), crictl logs <id>, crictl inspect <id>",
    ]} />
    <CodeBlock code={`# Node pe SSH ke bina debug karo
oc debug node/<worker-node>
chroot /host

# Container list dekho (Docker nahi!)
crictl ps
crictl ps -a     # stopped bhi

# Container logs
crictl logs <container-id>

# kubelet status
systemctl status kubelet
journalctl -u kubelet -n 50

# CRI-O status
systemctl status crio
journalctl -u crio -n 50`} label="bash — node debug" />
    <InterviewQ items={[
      {q:"Docker kyun hata diya OCP4 mein?",a:"Docker ek monolithic daemon tha with root privileges. CRI-O lightweight, minimal, Kubernetes-specific hai — sirf CRI (Container Runtime Interface) implement karta hai. Faster, more secure (no daemon), OCP version ke saath tightly tested."},
      {q:"OVN-Kubernetes aur SDN (old) mein kya farak hai?",a:"OpenShift SDN OCP 4.16 mein deprecated, 4.17 mein removed. OVN-Kubernetes zyada scalable, iptables ki jagah OpenFlow rules use karta hai, better NetworkPolicy performance, egress firewall + NAT built-in."},
    ]} />
  </div>
);}

function S10() { return (
  <div>
    <Concept>Operators OCP4 ka asli jaadu hain. Samajh lo ek cheez: <strong style={{color:T.accent}}>Operator = domain knowledge encoded as software</strong>. Ek CRD watch karta hai, aur reconcile karta hai — bilkul controller manager ke loops jaisa, lekin custom domain ke liye.</Concept>
    <ASCII>{`Human SRE ka knowledge  →  code  →  Operator
"Agar Prometheus ka PVC bhar jaaye toh..."  →  reconcile logic
"Naya etcd member add karne pe kya karna"   →  automated runbook`}</ASCII>
    <InfoBox title="Log Yahin Confuse Hote Hain 🔴 — Do Alag Duniya" color={T.red} bgColor="#1a0808" borderColor="#78100F">
      <TTable headers={["","Cluster Operators","OLM-managed Operators"]} rows={[
        ["Kaun manage karta","CVO (Cluster Version Operator)","OLM (Operator Lifecycle Manager)"],
        ["Kaunse","Platform core ~35 (network, auth, ingress, monitoring, etcd, MCO...)","Add-on / Day-2 (ODF, AMQ, GitOps, Pipelines, Virtualization)"],
        ["Version","Cluster version se tied. Alag se upgrade nahi","Independently upgradable via Subscription channel"],
        ["Kahan dikhta","oc get co","oc get csv -A, oc get subscription -A"],
        ["Install kaise","Automatic, install ke waqt","OperatorHub (Console) ya Subscription YAML"],
      ]} />
      <div style={{marginTop:8,fontSize:12.5,color:T.yellow}}>
        <strong>Rule of thumb:</strong> Agar oc get co mein hai → CVO ka. Agar OperatorHub se aaya → OLM ka.
      </div>
    </InfoBox>
    <InterviewQ items={[
      {q:"Operator aur regular Deployment mein kya farka hai?",a:"Deployment sirf pods run karta hai. Operator ek CRD watch karta hai aur complex operational logic implement karta hai (backup, failover, scaling decisions, certificate rotation, config validation). Operator = application-specific SRE automation."},
      {q:"Koi oc get co mein GitOps kyun nahi milta?",a:"GitOps (Argo CD) OLM-managed operator hai — OperatorHub se install hota hai, independently versioned. Cluster Operators platform ke core components hain jo CVO deploy karta hai. Do different lifecycles."},
    ]} />
  </div>
);}

function S11() { return (
  <div>
    <Concept>CVO cluster ka <strong style={{color:T.accent}}>root controller</strong> hai. Ek hi cheez watch karta hai: <code style={{color:T.accent}}>ClusterVersion</code> object. Isi se pura cluster upgrade aur health management hota hai.</Concept>
    <ASCII>{`ClusterVersion object (desired state)
        ↓ CVO watch karta hai
Release Image (container image)
        ↓ mein sab cluster operators ka manifests + image digests
CVO → order mein (runlevel-wise) apply karta hai
        ↓ har Cluster Operator apna kaam karta hai
Cluster converges to desired version`}</ASCII>
    <InfoBox title="Cluster Operator ke 3 Conditions — APD" color={T.accent}>
      <strong style={{color:T.green}}>Available=True</strong> → Kaam kar raha hai (minimum functionality)<br/>
      <strong style={{color:T.yellow}}>Progressing=True</strong> → Change ho raha hai (upgrade/rollout)<br/>
      <strong style={{color:T.red}}>Degraded=True</strong> → 🔴 Kuch toota hua hai — yahi dekho pehle<br/><br/>
      <strong style={{color:T.textPri}}>Healthy cluster = Available=True, Progressing=False, Degraded=False (sab ke liye)</strong>
    </InfoBox>
    <CodeBlock code={`# Cluster health — YE PEHLA COMMAND HAI HAR BAAR
oc get co

# Expected output:
# NAME             VERSION   AVAILABLE   PROGRESSING   DEGRADED
# authentication   4.20.3    True        False         False
# etcd             4.20.3    True        False         False
# ingress          4.20.3    True        False         False

# Koi Degraded hai toh:
oc describe co <name>    # Message field mein reason milega

# Cluster version + upgrade history
oc get clusterversion
oc describe clusterversion version | head -40`} label="bash — CVO commands" />
    <WarnBox items={[
      "CVO drift ko revert karta hai. Agar manually kisi core operator ka deployment edit kiya, CVO usko wapas laa dega. unmanaged overrides production mein avoid karo — support contract toot jaata hai.",
    ]} />
    <InterviewQ items={[
      {q:"Cluster upgrade kaise kaam karta hai?",a:"oc adm upgrade --to=4.20.x → CVO release image pull karta hai → manifests runlevel-wise apply → har Cluster Operator update hota hai → eventually ClusterVersion status updated. Rollback possible (oc adm upgrade --to=<previous>) agar upgrade failed."},
    ]} />
  </div>
);}

function S12() { return (
  <div>
    <Concept>MCO node ka OS-level configuration manage karta hai. RHCOS immutable hai, isliye <strong style={{color:T.accent}}>har OS-level change MachineConfig se hi possible hai</strong> — SSH se nahi.</Concept>
    <ASCII>{`MachineConfig (chhote config tukde — files, kernel args, systemd units)
      ↓  MCO merge karta hai, per-pool
rendered-master-<hash> / rendered-worker-<hash>   ← final merged config
      ↓
MachineConfigPool (MCP: master, worker, custom "infra", "edge")
      ↓
machine-config-daemon (har node pe DaemonSet)
      ↓
Node: CORDON → DRAIN → write files → REBOOT → UNCORDON`}</ASCII>
    <CodeBlock code={`oc get mc           # sab MachineConfigs
oc get mcp          # MachineConfigPools — ye roz dekho!
oc get nodes        # koi SchedulingDisabled? drain chal raha hai

# MCP output padhna seekho:
# NAME     CONFIG                   UPDATED  UPDATING  DEGRADED  MACHINES  READY
# master   rendered-master-a1b2c3   True     False     False     3         3
# worker   rendered-worker-d4e5f6   False    True      False     50        48
# ↑ worker pool update ho raha hai, 48/50 done`} label="bash — MCO commands" />
    <CodeBlock code={`# Example: worker pe custom file drop karo
apiVersion: machineconfiguration.openshift.io/v1
kind: MachineConfig
metadata:
  labels:
    machineconfiguration.openshift.io/role: worker   # kis pool ke liye
  name: 99-worker-custom-motd                        # 99- = late ordering
spec:
  config:
    ignition:
      version: 3.4.0
    storage:
      files:
      - path: /etc/motd
        mode: 0644
        overwrite: true
        contents:
          source: data:text/plain;charset=utf-8;base64,QWlydGVsIEVkZ2UgLSBPQ1AgNC4yMAo=`} label="yaml — MachineConfig" />
    <TTable headers={["Field","Kya matlab"]} rows={[
      ["role label","Kis MCP mein merge hoga — worker, master, ya custom pool"],
      ["name: 99-...","MachineConfigs naam ke lexical order mein merge. 99- = custom (Red Hat ke 00-, 01- ke baad)"],
      ["ignition.version: 3.4.0","OCP 4.20 ka Ignition spec. Galat version = MC reject"],
      ["contents.source","data URL hona chahiye (base64 encoded). Raw text nahi chalta."],
    ]} />
    <WarnBox items={[
      "Har MachineConfig change = rolling REBOOT of whole pool. 200-node cluster + maxUnavailable: 1 = 200 sequential reboots = ghanton ka kaam.",
      "Production mein maxUnavailable: 10% set karo. Test cluster pe pehle test karo. Change window plan karo.",
      "MCP paused: true karke bhool mat jaana — unpause pe sab ek saath reboot. #1 production galti.",
    ]} />
    <InterviewQ items={[
      {q:"MachineConfig ka naam 99- se kyun shuru karna chahiye?",a:"MachineConfigs lexical order mein merge hote hain. Red Hat ke platform configs 00-, 01-, 05- se start hote hain. Custom configs 99- se shuru karke ensure karte ho ki aapka config baad mein apply ho aur override ho sake."},
      {q:"MCP paused kab karte hain?",a:"Maintenance window mein jab aap node reboots rokna chahte ho. Ye changes queue karta rehta hai. Unpause pe sab pending changes ek saath apply hote hain — isliye carefully plan karo."},
    ]} />
  </div>
);}

function S13() { return (
  <div>
    <Concept>OLM = Day-2 operators ka lifecycle manager. Platform operators (CVO) ke alawa ke sab add-on operators OLM manage karta hai.</Concept>
    <TTable headers={["Object","Analogy","Kaam"]} rows={[
      ["CatalogSource","App store ka catalog","Available operators ki list (redhat-operators, certified, community, private mirror)"],
      ["Subscription","'Is app ko install karo + is channel pe rakho'","Operator install + auto-update channel (stable, fast, candidate)"],
      ["InstallPlan","Installation ka plan","Manual/Automatic approval mode. Manual = prod safety."],
      ["CSV (ClusterServiceVersion)","App ka version record","Installed operator ka manifest — permissions, CRDs, deployment, version"],
      ["OperatorGroup","Scope define karo","Operator kis namespace(s) ko watch karega"],
    ]} />
    <CodeBlock code={`oc get catalogsource -n openshift-marketplace
oc get subscription -A
oc get csv -A
oc get installplan -A

# Specific operator ki subscription dekho
oc get subscription openshift-gitops-operator -n openshift-operators -o yaml`} label="bash — OLM commands" />
    <WarnBox items={[
      "Subscription mein installPlanApproval: Manual rakho critical operators (ODF, Virtualization) ke liye — warna aadhi raat ko auto-upgrade ho jaayega. 🔴",
      "Disconnected cluster mein: mirror registry + ImageDigestMirrorSet + private CatalogSource zaroori hai. oc-mirror plugin use karo.",
    ]} />
    <InterviewQ items={[
      {q:"CSV kya hota hai?",a:"ClusterServiceVersion = installed operator ka 'package info'. Uski version, CRDs jo install karega, RBAC permissions, deployment spec, API descriptors — sab isme hota hai. oc get csv -A se installed operators aur unki versions milti hain."},
      {q:"Subscription channel kya hota hai?",a:"Channel = upgrade track. stable (tested, slow), fast (latest, aggressive), candidate (beta). Production mein stable. Ek Subscription ek hi channel pe hota hai — change karna = manual action."},
    ]} />
  </div>
);}

function S14() { return (
  <div>
    <Concept>RHCOS = Red Hat Enterprise Linux CoreOS. Immutable OS jisme /usr read-only hai. Sab config MachineConfig se aata hai. Ignition sirf first boot pe chalta hai.</Concept>
    <TTable headers={["RHCOS Feature","Detail"]} rows={[
      ["Immutable /usr","Read-only. Package manager se install allowed nahi. Change = MachineConfig."],
      ["rpm-ostree","Atomic OS updates. Poora OS image swap, purana deployment rollback ke liye rehta hai."],
      ["SELinux","Enforcing. Container escape ke against extra layer. Volume mount issues ka common source."],
      ["RHCOS mandatory","Control plane pe RHCOS mandatory. Worker pe strongly recommended (RHEL worker deprecated)."],
    ]} />
    <CodeBlock code={`# RHCOS ka inside dekho
oc debug node/<worker-node>
chroot /host

rpm-ostree status        # current (●) + rollback deployment dikhega
touch /usr/testfile      # ❌ Read-only file system
touch /etc/testfile      # ✅ /etc aur /var writable hain
exit; exit`} label="bash — RHCOS inspection" />
    <InfoBox title="Ignition — Sirf First Boot">
      Ignition initramfs stage mein chalta hai — systemd se bhi pehle. Disk partition, filesystem, files, users, systemd units set karta hai.
      <strong style={{color:T.textPri}}> Phir kabhi dobara nahi chalta.</strong><br/><br/>
      Node boot → <strong style={{color:T.accent}}>Machine Config Server (MCS)</strong> se Ignition config maangta hai
      (<code style={{color:T.accent}}>https://api-int.cluster:22623/config/worker</code>) → apply → boot complete.
    </InfoBox>
    <InfoBox title="Port 22623 — Firewall Pe Khula Hona Zaroori" color={T.red} bgColor="#1a0808" borderColor="#78100F">
      MCS port 22623 (masters → all nodes) firewall pe open hona chahiye. UPI install failure ka classic root cause yahi hai.
      API port = 6443. Apps/ingress = 80, 443.
    </InfoBox>
    <TTable headers={["","Ignition","cloud-init"]} rows={[
      ["Kab chalta","Sirf first boot (initramfs, pre-systemd)","Har boot pe (ya conditionally)"],
      ["System modify","Boot se pehle — partition, filesystem, files","Running system ko modify karta hai"],
      ["Predictability","Very high — system always same state se start","Lower — varies by boot"],
      ["OCP mein","MCO → Ignition JSON generate → MCS serve karta hai","Nahi use hota"],
    ]} />
    <InterviewQ items={[
      {q:"Ignition aur cloud-init mein kya farak hai?",a:"Ignition sirf pehli baar chalta hai (pre-systemd, initramfs stage), disk + files set karta hai. cloud-init har boot pe chal sakta hai running system pe. Ignition zyada predictable aur secure hai — system hamesha same deterministic state mein start hota hai."},
      {q:"RHCOS pe SSH karke config kyun nahi karna chahiye?",a:"Manual change next MachineConfig reconcile ya reboot pe udd jaayega. Worse: node Degraded mark ho sakta hai. Sahi tariqa = MachineConfig likhna aur MCO se apply karna — declarative, auditable, Git mein tracked."},
    ]} />
  </div>
);}

function S15() { return (
  <div>
    <Concept>oc create deployment se container chalne tak poora journey — ye 12-step flow architect interview ka centerpiece hai.</Concept>
    <InfoBox title="Mantra — yaad rakho" color={T.accent}>
      <strong>"API server likhta hai, Controller sochta hai, Scheduler chunta hai, Kubelet karta hai, CRI-O chalata hai."</strong>
    </InfoBox>
    <ASCII>{`1. USER: oc create deployment nginx --image=nginx
   (oc reads ~/.kube/config → cluster URL + cert/token)
             ↓ HTTPS POST → api.<cluster>:6443 (LB → 3 masters)
2. KUBE-APISERVER
   2a. Authentication  → token valid? user = sumit
   2b. Authorization   → RBAC: can sumit create deployments in 'demo'? ✅
   2c. Mutating admission → SCC inject happens later on Pod
   2d. Validation      → schema OK?
   2e. Validating admission → ResourceQuota, LimitRange
             ↓
3. etcd: /registry/deployments/demo/nginx  ← WRITE (ye "sach" ban gaya)
             ↓ (apiserver → WATCH event)
4. DEPLOYMENT CONTROLLER
   "Naya Deployment, replicas=1, koi RS nahi"
   → CREATE ReplicaSet nginx-abc123
             ↓
5. REPLICASET CONTROLLER
   "RS chahta 1 pod, ground pe 0"
   → CREATE Pod (spec.nodeName = "" → PENDING)
             ↓
6. SCC ADMISSION 🔴 (OpenShift-specific!)
   Pod ka ServiceAccount → SCC lookup → restricted-v2
   → runAsUser random UID inject (e.g. 1000680000)
   → drop ALL capabilities, seccomp=RuntimeDefault
   (root maanga → REJECT — yahi "works in K8s, fails OCP" wali baat)
             ↓
7. SCHEDULER
   Filter → Score → Binding: pod.spec.nodeName = worker-03 → etcd
             ↓
8. KUBELET on worker-03  ("mere node ka pod aaya!")
   → CNI (OVN-K8s): pod ko IP + veth + OVS port
   → CSI: volumes attach/mount
   → CRI-O: image pull → container create → start
             ↓
9. CRI-O → runc → Linux namespaces + cgroups + SELinux label → PROCESS RUNNING
             ↓
10. KUBELET status report → apiserver → etcd → Pod: Running ✅
             ↓ (agar Service + Route bana)
11. OVN programs flows; Ingress Operator HAProxy reload → external traffic in`}</ASCII>
    <CodeBlock code={`# Is flow ko real mein dekho:
oc new-project m1-lab
oc create deployment web --image=registry.access.redhat.com/ubi9/httpd-24
oc get events -n m1-lab --sort-by=.lastTimestamp

# SCC kya laga? (OpenShift-specific!)
oc get pod -n m1-lab -o yaml | grep -A2 "openshift.io/scc\|runAsUser"

# Expected:
# openshift.io/scc: restricted-v2
# runAsUser: 1000680000   ← random UID! SCC ne inject kiya, aapne nahi`} label="bash — flow trace" />
    <InterviewQ items={[
      {q:"SCC kab evaluate hota hai request flow mein?",a:"Mutating admission webhook phase mein — API server pe, pod etcd mein likhne se pehle. Pod ka ServiceAccount dekha jaata hai, us SA ki allowed SCCs check hoti hain, best matching SCC select hoti hai, aur pod spec mutate hota hai (UID inject, capabilities drop, etc.)."},
      {q:"Koi bata sakta hai pod Pending kyun tha jab scheduler ne bind kiya?",a:"Binding = scheduler ne pod.spec.nodeName etcd mein likh diya. Lekin kubelet us node pe pod status update karne mein thoda time leta hai — is gap mein pod Pending dikhta hai. Normal hai, 1-2 second mein Running ho jaana chahiye."},
    ]} />
  </div>
);}

function S16() { return (
  <div>
    <Concept>Installation method ka choice infrastructure, network constraints, aur site count pe depend karta hai — preference pe nahi.</Concept>
    <TTable headers={["","IPI","UPI","Assisted Installer","Agent-based"]} rows={[
      ["Infra kaun banata","Installer (Terraform)","Aap (Ansible/manual)","UI + ISO","Offline ISO, no bastion"],
      ["Control","Kam (opinionated)","Poora","Medium","High"],
      ["MachineSet/autoscaling","✅","❌ (bare metal)","Limited","❌"],
      ["Disconnected","Partial","✅","✅","✅ Best"],
      ["Kab use karein","Cloud (AWS/Azure/GCP/vSphere)","Bare metal, strict network","Connected/disconnected UI","Telco edge / far-edge 🌟"],
    ]} />
    <ASCII>{`Installation flow (UPI/IPI common steps):

install-config.yaml
      ↓ openshift-install create manifests
manifests/ (customize: network, MC, etc.)
      ↓ openshift-install create ignition-configs
bootstrap.ign  master.ign  worker.ign
      ↓
BOOTSTRAP NODE (temporary!) → single-node control plane
      ↓ masters boot → bootstrap se manifests + certs lete hain
      ↓ etcd quorum bana → real control plane up
      ↓ BOOTSTRAP DELETE KARO (bootstrap complete)
      ↓ CVO ne cluster operators deploy kiye
      ↓ workers join (CSR approve karo UPI mein!)
      ↓ install-complete`}</ASCII>
    <CodeBlock code={`# UPI classic gotcha: workers Pending — CSRs approve nahi
oc get csr -o name | xargs oc adm certificate approve
# (Do baar chalao — client CSR, phir serving CSR)

# DNS requirements (ye ratna padega!)
# api.<cluster>.<base>      → API LB   (6443, 22623)
# api-int.<cluster>.<base>  → API LB   (internal — MCS!)
# *.apps.<cluster>.<base>   → Ingress LB (80, 443)

# NTP bhi zaroori! etcd time-sensitive hai`} label="bash — install troubleshooting" />
    <InterviewQ items={[
      {q:"Telco edge ke liye Agent-based installer kyun best hai?",a:"Agent-based: fully disconnected, no bastion, no DHCP dependency, custom ISO per site, install-config + agent-config baked in ISO. 200+ sites ke liye ISO generation automated (pipeline). No network path to cloud/SaaS needed during install."},
      {q:"Bootstrap node kya karta hai aur kab delete hota hai?",a:"Bootstrap ek temporary single-node control plane chalata hai — masters initially bootstrap se manifests aur certs lete hain. Jab masters ka etcd quorum ban jaata hai aur real control plane up ho jaata hai, bootstrap ka kaam khatam. 'Bootstrap complete' message aane pe delete karo."},
    ]} />
  </div>
);}

function S17() { return (
  <div>
    <InfoBox title="Scenario: 200+ Bare-Metal OCP Edge Clusters — Indian Telecom" color={T.orange} bgColor="#1a0f06" borderColor="#78350F">
      Maharashtra, Delhi, TN circles mein distributed edge clusters.
    </InfoBox>
    <TTable headers={["Decision","Choice","Kyun"]} rows={[
      ["Cluster topology","3-node compact (masters schedulable)","Edge site pe 6 servers ka luxury nahi. Trade-off: etcd + workload same nodes — strict resource reservation mandatory."],
      ["Install method","Agent-based installer","Fully disconnected, no bastion, no DHCP dependency. 200 sites ke liye automated ISO generation pipeline."],
      ["etcd disk","NVMe non-negotiable","SATA SSD bhi fail hoti hai high I/O pe. etcd_disk_wal_fsync_duration_seconds p99 < 10ms alert Day-0 se."],
      ["MCO","Custom MachineConfigPool 'edge-mcp'","Kernel args (hugepages, isolcpus, SR-IOV) MachineConfig se. maxUnavailable tune karo. Blast radius control."],
      ["Fleet management","RHACM hub-spoke","200 clusters manually manage nahi hote. CVO version pinning + policy-driven upgrade waves (canary 5 → 50 → rest)."],
      ["OLM","Disconnected mirror + ImageDigestMirrorSet","installPlanApproval: Manual for ODF/Virtualization."],
      ["Observability","In-cluster Prometheus → remote-write → Thanos","Central query layer. Module 7 mein detail."],
    ]} />
    <InfoBox title="Banking Contrast" color={T.accent}>
      3 masters + 3 infra + N workers. Infra nodes pe router/monitoring/registry (licensing cost — infra nodes subscription-exempt).
      Compliance Operator + PCI-DSS profile. etcd encryption at rest. Dedicated egress IPs.
    </InfoBox>
    <InfoBox title="Real Production Incident (Edge)" color={T.red} bgColor="#1a0808" borderColor="#78100F">
      <strong>Symptom:</strong> 3-node edge cluster, etcd intermittently Degraded, API server slow, pods randomly evict.<br/>
      <strong>Investigation:</strong> etcdctl endpoint status → "leader changed" bar-bar. Prometheus: etcd_disk_wal_fsync_duration_seconds p99 = 180ms.<br/>
      <strong>Root cause:</strong> SATA SSD pe container storage + etcd dono → I/O contention.<br/>
      <strong>Fix:</strong> etcd dedicated NVMe pe (MachineConfig se mount). Hardware BOM standardize kiya.<br/>
      <strong>Architect lesson:</strong> <em>etcd disk latency is the single most common cause of mysterious OCP instability. Monitor it from Day-0.</em>
    </InfoBox>
  </div>
);}

function S18() { return (
  <div>
    <Concept>In labs ko chalao aur har output ko padho — sirf pass mat karo. Ye real cluster ke baare mein intuition build karte hain.</Concept>
    <InfoBox title="Lab 1.1 — Cluster X-Ray">
      <CodeBlock code={`oc get clusterversion
oc describe clusterversion version | head -40
oc get co
# DEGRADED=True dikhe toh:
oc describe co <name>   # Message field mein exact reason`} label="bash" />
    </InfoBox>
    <InfoBox title="Lab 1.2 — Nodes aur OS">
      <CodeBlock code={`oc get nodes -o wide
oc get node <node> -o jsonpath='{.status.nodeInfo}' | python3 -m json.tool
# containerRuntimeVersion: cri-o://1.33.x    ← Docker nahi!
# osImage: Red Hat Enterprise Linux CoreOS   ← RHCOS
# kubeletVersion: v1.33.x  (OCP 4.20 → K8s 1.33, formula: 4.x → 1.(x+13))`} label="bash" />
    </InfoBox>
    <InfoBox title="Lab 1.3 — RHCOS Immutability">
      <CodeBlock code={`oc debug node/<worker-node>
chroot /host
rpm-ostree status          # current (●) + rollback
touch /usr/testfile        # ❌ Read-only file system
touch /etc/testfile        # ✅ /etc aur /var writable hain
exit; exit`} label="bash" />
    </InfoBox>
    <InfoBox title="Lab 1.4 — MachineConfig Apply aur Reboot Dekho">
      <CodeBlock code={`oc apply -f 99-worker-motd.yaml
watch oc get mcp worker
# worker  rendered-worker-OLD  False  True  False  3  2  ← UPDATING
# ...node cordon → drain → file write → REBOOT → uncordon...
# worker  rendered-worker-NEW  True   False False  3  3  ← UPDATED

oc get nodes           # SchedulingDisabled dikhega (drain phase)
oc logs -n openshift-machine-config-operator ds/machine-config-daemon \\
  -c machine-config-daemon --tail=30`} label="bash" />
    </InfoBox>
    <InfoBox title="Lab 1.5 — OLM duniya">
      <CodeBlock code={`oc get catalogsource -n openshift-marketplace
oc get packagemanifests -n openshift-marketplace | head
oc get subscription -A
oc get csv -A | head`} label="bash" />
    </InfoBox>
    <InfoBox title="Lab 1.6 — Pod ka Journey Trace">
      <CodeBlock code={`oc new-project m1-lab
oc create deployment web --image=registry.access.redhat.com/ubi9/httpd-24
oc get events -n m1-lab --sort-by=.lastTimestamp
# Scaled up replica set...    ← deployment-controller
# Created pod: web-xxxx-yyyy  ← replicaset-controller
# Successfully assigned... to worker-2   ← SCHEDULER
# Pulling image ...            ← kubelet → CRI-O
# Started container            ← CRI-O

# SCC check
oc get pod -n m1-lab -o yaml | grep -A2 "openshift.io/scc\|runAsUser"
# openshift.io/scc: restricted-v2
# runAsUser: 1000680000   ← random UID, SCC ne inject kiya`} label="bash" />
    </InfoBox>
  </div>
);}

function S19() { return (
  <div>
    <Concept>Troubleshooting decision tree — kuch bhi toota ho, is flow ko follow karo.</Concept>
    <ASCII>{`CLUSTER MEIN KUCH TOOTA HAI?
        │
        ├─ oc get co  →  koi DEGRADED=True?
        │     YES → oc describe co <name>
        │           ├─ authentication: OAuth/IdP/route/cert issue
        │           ├─ ingress: router pods? LB? DNS *.apps?
        │           ├─ image-registry: PVC/S3 missing
        │           ├─ etcd: disk latency? member down? clock skew?
        │           └─ machine-config: oc get mcp → DEGRADED pool
        │     NO  ↓
        ├─ oc get nodes  →  koi NotReady?
        │     → oc describe node <n>  (MemoryPressure? DiskPressure?)
        │     → oc debug node/<n> → chroot /host
        │         systemctl status kubelet
        │         journalctl -u kubelet -n 100
        │         crictl ps -a
        │     SchedulingDisabled → MCO drain (normal) ya drain STUCK?
        │         → PDB blocking eviction?
        │
        ├─ oc get pods -A | grep -v Running | grep -v Completed
        │     Pending      → oc describe pod → scheduler reason
        │     ImagePullBackOff → registry auth? ICSP/IDMS?
        │     CrashLoopBackOff → oc logs <pod> --previous  ← MAT BHOOLNA
        │     CreateContainerConfigError → missing Secret/ConfigMap
        │     (pod hi nahi bana) → oc get events → "unable to validate SCC"🔴
        │
        └─ etcd health (cluster slow/flapping)
              oc rsh -n openshift-etcd etcd-<master-0>
              etcdctl endpoint status --cluster -w table
              → "took too long" / high fsync → DISK PROBLEM`}</ASCII>
    <CodeBlock code={`# Sabse important troubleshooting commands
oc get co                                    # cluster health, one shot
oc adm must-gather                           # Red Hat support ke liye
oc get events -A --sort-by=.lastTimestamp | tail -30
oc adm node-logs <node> -u kubelet           # SSH ke bina node logs!
oc adm node-logs <node> -u crio
oc adm top nodes
oc adm top pods -A
oc get mcp                                   # MCO stuck? yahin dikhega
oc logs <pod> --previous                     # crash ho chuke container ka log`} label="bash — troubleshooting toolkit" />
    <InterviewQ items={[
      {q:"CrashLoopBackOff debug kaise karein?",a:"pehle oc logs <pod> --previous — crash ho chuke container ka log milta hai. Bina --previous ke current container ka log milta hai jo shayad abhi start hi ho raha ho. oc describe pod se health probe failures, OOMKilled, exit codes bhi milte hain."},
      {q:"Pod bana hi nahi, SCC error kya matlab?",a:"'unable to validate against any security context constraint' = pod ke ServiceAccount ke paas koi bhi SCC nahi jo pod ki securityContext demands satisfy kare. ServiceAccount ko oc adm policy add-scc-to-user se sahi SCC do."},
    ]} />
  </div>
);}

function S20() { return (
  <div>
    <Concept>Common mistakes avoid karo — ye real production incidents se aaye hain.</Concept>
    <TTable headers={["❌ Galti","✅ Sahi"]} rows={[
      ["Node pe SSH karke vi /etc/... edit","MachineConfig likho. Manual edit next reboot pe udd jaayega ya node degraded hoga."],
      ['"Docker se debug karta hoon"',"Docker hai nahi. crictl use karo."],
      ["oc get co kabhi nahi dekhna","Cluster ka health dashboard. Pehla command yahi ho."],
      ["Container ko root chalane ki koshish","SCC block karega. restricted-v2 samjho, image ko rootless banao (arbitrary UID)."],
      ["MCP paused: true karke bhool jaana","Configs queue hoti hain; unpause pe mass reboot. 🔴"],
      ["MachineConfig ko chhota change samajhna","Har MC = rolling reboot of whole pool."],
      ["Cluster operator ka deployment edit karna","CVO revert kar dega. Sahi = uska CR edit karo (IngressController, Network)."],
      ["installPlanApproval: Automatic for critical ops","Manual rakho — surprise upgrades tod dete hain."],
      ["etcd ko slow disk pe rakhna","NVMe. Non-negotiable."],
      ["Even number of masters","Quorum = 3 ya 5. 2 ya 4 kabhi nahi."],
    ]} />
    <Concept style={{marginTop:16}}>Best Practices (Enterprise):</Concept>
    {[
      "3 masters + 3 infra + N workers (bade clusters). Infra nodes pe router + monitoring + registry → subscription cost bachta hai.",
      "Infra nodes pe taint + operators ko nodeSelector/tolerations se pin karo (Ingress, Monitoring, Registry CR mein nodePlacement field).",
      "etcd: dedicated NVMe, fsync p99 < 10ms alert, automated daily backup + offsite copy, encryption at rest.",
      "Custom MachineConfigPools — worker, infra, gpu, edge. Blast radius control.",
      "Upgrades: EUS→EUS for big fleets; canary cluster → staging → prod waves.",
      "Disconnected/mirror registry — oc-mirror plugin + ImageDigestMirrorSet.",
      "ResourceQuota + LimitRange har namespace pe — Day-1 se.",
      "systemReserved/kubeReserved set karo (KubeletConfig) — compact clusters mein must.",
      "GitOps everything — MachineConfigs, operators, quotas sab Git mein.",
      "must-gather automate karo — incident pe pehle collect, phir fix.",
    ].map((bp,i)=>(
      <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
        <span style={{color:T.green,fontWeight:700,minWidth:24,marginTop:1}}>{i+1}.</span>
        <span style={{fontSize:13,color:T.textSec,lineHeight:1.6}}>{bp}</span>
      </div>
    ))}
  </div>
);}

function S21() { return (
  <div>
    <Concept>Security layers aur performance considerations — architect level awareness.</Concept>
    <InfoBox title="Security Layers">
      <TTable headers={["Layer","Module 1 mein kya samajhna"]} rows={[
        ["SCC","OCP ka signature feature. Default restricted-v2: no root, random UID, drop all caps. 'K8s pe chala OCP pe nahi' ka 90% reason."],
        ["RBAC","Pre-built roles: admin, edit, view, cluster-admin. Project = Namespace + RBAC + annotations."],
        ["SELinux","RHCOS pe enforcing. Container escape ke against extra layer. Volume mount issues ka common cause."],
        ["Certificates","Cluster khud rotate karta hai. 30+ din band raha toh certs expire — edge sites pe real problem."],
        ["Secrets","etcd mein by default base64, encrypted nahi. APIServer CR se encryption-at-rest on karo."],
        ["Network Policies","Default: sab pods baat kar sakte. Zero-trust ke liye default-deny NetworkPolicy per namespace."],
        ["Compliance Operator","CIS, PCI-DSS, NIST, STIG profiles. Banking/Gov mein mandatory."],
        ["etcd encryption","oc edit apiserver cluster → spec.encryption.type: aescbc"],
      ]} />
    </InfoBox>
    <InfoBox title="Performance Considerations" color={T.orange} bgColor="#1a0f06" borderColor="#78350F">
      <TTable headers={["Area","Architect ko kya pata hona chahiye"]} rows={[
        ["etcd","DB size limit ~8GB. Zyada objects/events = bloat. defrag periodically. Events ka TTL kam karo."],
        ["API server","Rate limits. 200-cluster fleet + aggressive controllers = API pressure."],
        ["Node sizing","Default max 250 pods/node. maxPods KubeletConfig se badha sakte ho lekin CIDR check karo."],
        ["Reserved resources","systemReserved na set karna = kubelet OOM = node NotReady. Compact cluster mein must."],
        ["Networking","OVN-Kubernetes control plane scale-sensitive. Bahut saare NetworkPolicies = OVS flow explosion."],
        ["Monitoring","Default retention 15d, memory hungry. PV size + retention tune karo, ya remote-write/Thanos."],
        ["cgroup v2","OCP 4.14+ default. Better memory accounting, MemoryQoS."],
      ]} />
    </InfoBox>
  </div>
);}

function S22() { return (
  <div>
    <TTable headers={["Feature","OCP 4.20","Vanilla K8s","EKS/AKS/GKE","Docker/Compose"]} rows={[
      ["OS management","RHCOS immutable, cluster-managed","Aapka kaam","Managed nodes","Host OS aapka"],
      ["Unit of compute","Pod (container)","Pod","Pod","Container"],
      ["Install","IPI/UPI/Agent/Assisted","kubeadm/kops","Console/CLI","docker run"],
      ["Upgrade","Single button, whole stack","Component-by-component","Control plane managed","N/A"],
      ["Security default","Non-root (SCC), SELinux","root allowed","root allowed","root by default"],
      ["Ingress","Built-in Router (HAProxy) + Route CRD","Install karo","ALB/AGIC","Nahi"],
      ["Registry","Built-in","Nahi","ECR/ACR/GCR","Docker Hub"],
      ["Monitoring","Built-in Prometheus stack","Nahi","CloudWatch","Nahi"],
      ["Support","Red Hat, single vendor","Community","Cloud vendor","Docker Inc"],
      ["Run VMs?","✅ OpenShift Virtualization (KubeVirt)","KubeVirt manually","Limited","❌"],
    ]} />
    <InfoBox title="OCP 3 vs OCP 4 — Interview Classic" color={T.yellow} bgColor="#1a1506" borderColor="#78500F">
      <TTable headers={["","OCP 3.11","OCP 4.x"]} rows={[
        ["Install","Ansible playbooks","openshift-install binary + Ignition"],
        ["OS","RHEL (mutable)","RHCOS (immutable)"],
        ["Runtime","Docker","CRI-O"],
        ["Node config","Ansible/manual","MachineConfig + MCO"],
        ["Upgrade","Ansible, risky","CVO, one-click, rollback-aware"],
        ["Add-ons","Manual","OperatorHub / OLM"],
        ["Networking","OpenShift SDN","OVN-Kubernetes (SDN removed 4.17+)"],
        ["Philosophy","Cluster is installed","Cluster manages itself"],
      ]} />
    </InfoBox>
  </div>
);}

function S23() { return (
  <div>
    <Concept>Admin sochta hai "kaise karun?". Architect sochta hai "kyun, kis cost pe, kis blast radius ke saath, aur 3 saal baad kya hoga?"</Concept>
    {[
      {t:"Blast Radius Thinking",c:T.accent,d:"MachineConfig ek pool ko touch karta hai. Isliye pools = failure domains. Custom MCP design = architect ka decision."},
      {t:"Upgrade Strategy",c:T.green,d:"200 clusters ek saath upgrade nahi karte. Canary waves, EUS channels, version skew policy (n-2 support). Ye roadmap hai, command nahi."},
      {t:"Compact vs Full Trade-off",c:T.yellow,d:"3-node compact hardware bachata hai lekin etcd aur workload same disk/CPU share karte hain. Reservation aur QoS classes ka discipline chahiye."},
      {t:"Install Method Choice",c:T.orange,d:"IPI = speed + MachineSet autoscaling. UPI = control lekin no autoscaling on bare metal. Agent = disconnected edge. Choice business constraint se aati hai, preference se nahi."},
      {t:"Cost Model",c:T.purple,d:"Infra nodes subscription-exempt. Router/monitoring/registry wahan daalne se license cost girta hai. Ye architect ka number hai."},
      {t:"CVO Override = Technical Debt",c:T.red,d:"unmanaged: true shortcut lagta hai lekin support contract toot jaata hai. Avoid karo."},
      {t:"Everything is an API",c:T.accent,d:"Agar aap kisi cheez ko GitOps se declaratively manage nahi kar sakte, toh wo aapka design flaw hai, platform ki limitation nahi."},
      {t:"Day-2 > Day-1",c:T.green,d:"Install ek din ka kaam hai. Upgrade, backup, DR, cert rotation, capacity — 3 saal ka kaam. Design usi ke liye karo."},
    ].map((n,i)=>(
      <div key={i} style={{background:T.card,borderLeft:`3px solid ${n.c}`,borderRadius:"0 8px 8px 0",
        padding:"12px 16px",marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:700,color:n.c,letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>{n.t}</div>
        <div style={{fontSize:13,color:T.textSec,lineHeight:1.6}}>{n.d}</div>
      </div>
    ))}
    <InfoBox title="Architect ki Ek-Line Definition of OCP4" color={T.purple} bgColor="#100a1a" borderColor="#2A1A45">
      "OpenShift 4 is a Kubernetes cluster that runs a set of Operators whose job is to keep the cluster — including its own operating system — converged to a version declared in a single ClusterVersion object."
    </InfoBox>
  </div>
);}

function S24() { return (
  <div>
    <TTable headers={["#","Concept","Ek line"]} rows={[
      ["1","OCP kya hai","Kubernetes + integrated platform + secure-by-default + Red Hat support + self-managing lifecycle"],
      ["2","OCP4 ka core idea","Cluster khud ko manage karta hai — Operators ke through"],
      ["3","etcd","Single source of truth, Raft, quorum = N/2+1, disk latency = jaan"],
      ["4","API Server","Ekmatra darwaza. Authn → Authz → Mutating → Validate → Validating → etcd"],
      ["5","Scheduler","Filter → Score → Bind. Pod start nahi karta, sirf node chunta hai"],
      ["6","Controller Manager","Reconciliation loops: desired vs actual"],
      ["7","kubelet","Node ka agent — API server se pods leta hai, CRI-O se chalwata hai"],
      ["8","CRI-O","Container runtime. Docker nahi. Debug = crictl"],
      ["9","CVO","PM. ClusterVersion watch karta hai, ~35 Cluster Operators deploy/reconcile karta hai"],
      ["10","Cluster Operators","oc get co — cluster ka health dashboard. Available/Progressing/Degraded"],
      ["11","MCO","Node OS config. MC → rendered config → MCD → cordon/drain/reboot"],
      ["12","MCP","Failure domain. maxUnavailable = reboot ki speed"],
      ["13","OLM","Play Store. CatalogSource → Subscription → InstallPlan → CSV"],
      ["14","RHCOS","Immutable OS. /usr read-only. rpm-ostree. SSH edit = paap"],
      ["15","Ignition","Sirf first boot. Disk/files/users. MCS port 22623 se aati hai"],
      ["16","Install methods","IPI (cloud), UPI (bare metal), Agent (disconnected edge)"],
      ["17","SCC","Non-root by default, random UID. 'K8s pe chala OCP pe nahi' ka reason"],
      ["18","Pehla command","oc get co — hamesha"],
    ]} />
    <InfoBox title="Memory Tricks 🧠">
      <div style={{marginBottom:8}}><strong style={{color:T.accent}}>API Server pipeline: AAM-VP</strong> — Authentication → Authorization → Mutating → Validation → Validating → Persist. <em>"Aam khaya, phir VP ban gaya" 🥭</em></div>
      <div style={{marginBottom:8}}><strong style={{color:T.accent}}>Pod ka safar</strong> — "API likhta, Controller sochta, Scheduler chunta, Kubelet karta, CRI-O chalata"</div>
      <div style={{marginBottom:8}}><strong style={{color:T.accent}}>Cluster Operator conditions: APD</strong> — Available / Progressing / Degraded. "APD = Aapki Problem Dikhegi" 😄</div>
      <div style={{marginBottom:8}}><strong style={{color:T.accent}}>MCO flow: CDRU</strong> — Cordon → Drain → Reboot → Uncordon</div>
      <div><strong style={{color:T.accent}}>4 stambh of OCP4: CROI</strong> — CVO · RHCOS · Operators · Ignition</div>
    </InfoBox>
    <CodeBlock code={`# Daily cheat sheet — ye commands roz chalte hain
oc get co                         # cluster health (#1)
oc get clusterversion             # version + upgrade status
oc get mcp                        # node config rollout
oc get nodes -o wide              # nodes + OS + runtime
oc get csv -A                     # OLM operators
oc get pods -A | grep -v Running
oc describe co <name>             # kyun degraded?
oc adm node-logs <node> -u kubelet
oc debug node/<node>
oc adm must-gather                # support bundle
oc get events -A --sort-by=.lastTimestamp | tail -30`} label="bash — daily ops" />
  </div>
);}

function QuizSection() {
  const qs = [
    {q:"OCP mein default container runtime kaun sa hai, aur Docker kyun nahi?",hint:"CRI-O. Docker daemon heavy tha aur OCP lifecycle se tied nahi tha. crictl use karo."},
    {q:"etcd members hamesha odd number mein kyun rakhte hain?",hint:"Raft quorum = N/2+1. Odd number mein split-brain impossible. 4 nodes: do equal halves = no majority."},
    {q:"oc get co kya dikhata hai? Uske 3 conditions kaunse hain?",hint:"Cluster Operators ka health. Available (kaam chal raha), Progressing (change ho raha), Degraded (kuch toota)."},
    {q:"MachineConfig apply ka step-by-step flow aur ek bada risk kya hai?",hint:"MC → MCO render → MCP → MCD → CORDON → DRAIN → file write → REBOOT → UNCORDON. Risk: poore pool ka rolling reboot."},
    {q:"CVO-managed aur OLM-managed operator mein 3 farak aur ek-ek example.",hint:"CVO: oc get co, cluster version tied, core platform. OLM: oc get csv, independently upgradable, OperatorHub. Example: network (CVO) vs ODF (OLM)."},
    {q:"oc create deployment se container chalne tak 8+ steps. SCC kahan?",hint:"User → API → (Authn, Authz, Admission) → etcd → Deployment-controller → RS-controller → [SCC mutating admission] → Scheduler → kubelet → CRI-O → Running"},
    {q:"Ignition aur cloud-init mein kya farak hai? Ignition ka design kyun important?",hint:"Ignition: sirf first boot, pre-systemd, disk/files. cloud-init: har boot pe, running system. Ignition = predictable, deterministic state."},
    {q:"3-node edge cluster pe etcd Degraded bar-bar. Top 3 suspects?",hint:"1. Disk latency (etcd_disk_wal_fsync p99 > 10ms). 2. NTP clock skew. 3. Network latency between masters. Check: etcdctl endpoint status -w table."},
    {q:"200 bare-metal edge clusters ke liye install method — justify karo.",hint:"Agent-based installer. Fully disconnected, no bastion, no DHCP, custom ISO per site, automated pipeline for 200 ISOs."},
    {q:"Fleet pe kernel parameter change — design karo. Blast radius control?",hint:"Custom MCP (edge-mcp), maxUnavailable: 10%, canary pool pehle, phir batch, Prometheus alerts on node SchedulingDisabled, MCP paused: true for safe windows."},
  ];
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>Module 1 Quiz</div>
        <h1 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:"0 0 8px"}}>Test Yourself</h1>
        <p style={{fontSize:13.5,color:T.textSec,margin:0,lineHeight:1.6}}>
          10 questions — Sections A→D (Beginner → Architect). Type your answer, then click Reveal Hint to check.
        </p>
      </div>
      {qs.map((item,qi)=>(
        <div key={qi} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:600,color:T.textMute,letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>
            {qi<3?"Section A — Beginner":qi<5?"Section B — Intermediate":qi<7?"Section C — Advanced":"Section D — Architect"}
          </div>
          <div style={{fontSize:13.5,fontWeight:600,color:T.textPri,marginBottom:10,lineHeight:1.5}}>Q{qi+1}. {item.q}</div>
          <textarea
            value={answers[qi]||""}
            onChange={e=>setAnswers(a=>({...a,[qi]:e.target.value}))}
            placeholder="Type your answer here…"
            style={{width:"100%",minHeight:72,background:"#060D18",border:`1px solid ${T.border}`,
              borderRadius:7,padding:"8px 12px",color:T.textPri,fontSize:12.5,fontFamily:"inherit",
              resize:"vertical",outline:"none",lineHeight:1.6,boxSizing:"border-box"}}
          />
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={()=>setRevealed(r=>({...r,[qi]:!r[qi]}))}
              style={{fontSize:12,padding:"5px 12px",borderRadius:6,border:`1px solid ${T.accent}44`,
                background:"#071929",color:T.accent,cursor:"pointer"}}>
              {revealed[qi]?"Hide Hint":"Reveal Hint"}
            </button>
          </div>
          {revealed[qi]&&(
            <div style={{marginTop:10,background:"#0a1a10",border:`1px solid ${T.green}44`,
              borderRadius:8,padding:"10px 14px",fontSize:12.5,color:T.textSec,lineHeight:1.65}}>
              <span style={{color:T.green,fontWeight:600}}>Key points: </span>{item.hint}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home",  icon:"🏠", title:"Overview",                  sub:"Module map aur objectives"},
  {id:"s1",   icon:"❓", title:"Why OCP Exists?",            sub:"Business problem, DIY Kubernetes ka dard"},
  {id:"s2",   icon:"🎯", title:"5 Problems Solved",          sub:"Assembly, Security, Drift, Upgrade, Day-2"},
  {id:"s3",   icon:"🚗", title:"Real-world Analogies",       sub:"Engine vs Car, Sarkar model"},
  {id:"s4",   icon:"📅", title:"History in 5 Minutes",       sub:"v1→v4, philosophy shift"},
  {id:"s5",   icon:"🗂", title:"Cluster Architecture",       sub:"Control Plane vs Worker, two planes"},
  {id:"s6",   icon:"🚪", title:"API Server Deep Dive",       sub:"Pipeline: AAM-VP, 3 API servers"},
  {id:"s7",   icon:"💾", title:"etcd Deep Dive",             sub:"Raft, quorum, disk = jaan"},
  {id:"s8",   icon:"🔄", title:"Scheduler & Controller Mgr", sub:"Filter→Score→Bind, reconciliation loop"},
  {id:"s9",   icon:"⚙️", title:"Worker Node Components",     sub:"kubelet, CRI-O, OVN, MCD"},
  {id:"s10",  icon:"🤖", title:"Operators — Introduction",   sub:"CVO-managed vs OLM-managed, do duniya"},
  {id:"s11",  icon:"👑", title:"CVO & Cluster Operators",    sub:"PM analogy, APD conditions, upgrade"},
  {id:"s12",  icon:"🔧", title:"MCO & MachineConfig",        sub:"Immutable OS config, reboot risk"},
  {id:"s13",  icon:"🛒", title:"OLM — Play Store",           sub:"CatalogSource, Subscription, CSV"},
  {id:"s14",  icon:"🪨", title:"RHCOS & Ignition",           sub:"Immutable OS, first-boot only, port 22623"},
  {id:"s15",  icon:"🔍", title:"Request Flow End-to-End",    sub:"oc create → Running (12 steps)"},
  {id:"s16",  icon:"🏗", title:"Installation Methods",       sub:"IPI, UPI, Assisted, Agent-based"},
  {id:"s17",  icon:"📡", title:"Production: Telecom Edge",   sub:"200+ bare-metal clusters, architect decisions"},
  {id:"s18",  icon:"🧪", title:"Hands-on Labs",              sub:"Labs 1.1–1.6, chalao aur padho"},
  {id:"s19",  icon:"🔥", title:"Troubleshooting Tree",       sub:"Decision tree, toolkit, etcd pattern"},
  {id:"s20",  icon:"✅", title:"Mistakes & Best Practices",  sub:"Common pitfalls, enterprise standards"},
  {id:"s21",  icon:"🔐", title:"Security & Performance",     sub:"SCC, SELinux, etcd, network, cgroup v2"},
  {id:"s22",  icon:"📊", title:"Comparison Tables",          sub:"OCP vs vanilla vs cloud, OCP3 vs OCP4"},
  {id:"s23",  icon:"🏛", title:"Architect Notes",            sub:"Blast radius, cost, Day-2 thinking"},
  {id:"s24",  icon:"🧠", title:"Revision & Memory Tricks",   sub:"One-pager, mnemonics, daily cheat sheet"},
  {id:"quiz", icon:"📝", title:"Quiz",                       sub:"10 questions, Beginner → Architect"},
];

const sectionComponents = {
  s1:S1,s2:S2,s3:S3,s4:S4,s5:S5,s6:S6,s7:S7,s8:S8,s9:S9,s10:S10,
  s11:S11,s12:S12,s13:S13,s14:S14,s15:S15,s16:S16,s17:S17,s18:S18,
  s19:S19,s20:S20,s21:S21,s22:S22,s23:S23,s24:S24,quiz:QuizSection,
};

function HomePage({ onNavigate }) {
  return (
    <div>
      <div style={{marginBottom:28}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>
          MODULE 1 · OPENSHIFT 4.20
        </div>
        <h1 style={{fontSize:26,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.3}}>
          Overview & Architecture<br/>
          <span style={{color:T.accent}}>Architect-level Notes · Hinglish mein</span>
        </h1>
        <p style={{fontSize:14,color:T.textSec,lineHeight:1.7,maxWidth:580}}>
          "Why pehle, How baad mein." — Kubernetes se OCP ka safar, CVO se Ignition tak,
          business problem se production architect decisions tak. Har concept mein analogy,
          diagram, lab, aur interview prep.
        </p>
      </div>
      <ASCII>{`History → K8s vs OCP → Cluster Anatomy
   ↓
Control Plane (API Server, etcd, Scheduler, Controller Manager)
   ↓
Worker Node (kubelet, CRI-O, kube-proxy/OVN)
   ↓
"OpenShift ka jaadu" = Operators
   ↓
CVO → Cluster Operators → MCO → OLM → Day-2 Operators
   ↓
Node ka OS = RHCOS + Ignition
   ↓
Installation = IPI vs UPI vs Assisted vs Agent`}</ASCII>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:28,marginTop:20}}>
        {[{num:"25",label:"Sections"},{num:"12+",label:"Code Labs"},{num:"20+",label:"Interview Qs"},{num:"10",label:"Quiz Questions"}]
          .map((s,i)=>(
          <div key={i} style={{background:T.card,borderRadius:10,padding:"12px 14px",border:`1px solid ${T.border}`,textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:700,color:T.accent}}>{s.num}</div>
            <div style={{fontSize:11,color:T.textMute,marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>(
          <button key={s.id} onClick={()=>onNavigate(s.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:"12px 14px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",
              display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
            <div>
              <div style={{fontSize:11,color:T.textMute,marginBottom:2}}>
                {s.id==="quiz"?"Self-Test":`Section ${i+1}`}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:11,color:T.textSec}}>{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function OpenshiftOverview() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev=>new Set([...prev,id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s=>s.id===current);
  const prevSection = currentIdx>0 ? SECTIONS[currentIdx-1] : null;
  const nextSection = currentIdx<SECTIONS.length-1 ? SECTIONS[currentIdx+1] : null;
  const CurrentComponent = sectionComponents[current];
  const progressPct = Math.round((visited.size/SECTIONS.length)*100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0,zIndex:10}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🔴 OCP 4.20 — Overview & Architecture</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",background:"#ef4444",borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:"#ef4444",minWidth:36,textAlign:"right",fontWeight:600}}>{progressPct}%</span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {sidebarOpen&&(
          <div style={{width:230,minWidth:230,background:T.surface,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map((s,i)=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id?"rgba(239,68,68,0.1)":"none",fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id?"2px solid #ef4444":"2px solid transparent"}}>
                  <span style={{fontSize:12,minWidth:18}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11,
                      color:current===s.id?"#ef4444":visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.3}}>
                      {s.id==="home"||s.id==="quiz" ? s.title : `${i}. ${s.title}`}
                    </div>
                  </div>
                  {visited.has(s.id)&&current!==s.id&&<span style={{fontSize:10,color:T.green}}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:900,margin:"0 auto",padding:"24px 28px"}}>
            {current==="home" ? (
              <HomePage onNavigate={navigate} />
            ) : (
              <div>
                <div style={{marginBottom:24}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#ef4444",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>
                    {current==="quiz"?"Self-Test":`Section ${currentIdx} of ${SECTIONS.length-2}`}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <span style={{fontSize:24}}>{SECTIONS[currentIdx].icon}</span>
                    <h1 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:0}}>{SECTIONS[currentIdx].title}</h1>
                  </div>
                  <p style={{fontSize:13.5,color:T.textSec,margin:0,lineHeight:1.6}}>{SECTIONS[currentIdx].sub}</p>
                </div>
                {CurrentComponent&&<CurrentComponent />}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:32,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
                  {prevSection?(
                    <button onClick={()=>navigate(prevSection.id)}
                      style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,
                        padding:"10px 16px",cursor:"pointer",color:T.textSec,fontFamily:"inherit",fontSize:13,textAlign:"left"}}>
                      ← {prevSection.icon} {prevSection.title}
                    </button>
                  ):<div/>}
                  {nextSection&&(
                    <button onClick={()=>navigate(nextSection.id)}
                      style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.4)",
                        borderRadius:9,padding:"10px 16px",cursor:"pointer",color:"#ef4444",
                        fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                      {nextSection.icon} {nextSection.title} →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
